import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Network } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const empty = {
  name: "", listen_host: "0.0.0.0", listen_port: 443,
  upstream: "10.0.1.10:8080", protocol: "https", tls: true,
  rate_limit_rps: 1000, enabled: true, description: "",
};

export default function ProxyRules() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/proxy-rules");
      setItems(data);
    } catch (err) {
      console.error("proxy load failed", err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      if (editing) await api.put(`/proxy-rules/${editing}`, form);
      else await api.post("/proxy-rules", form);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); setEditing(null); setForm(empty); await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this proxy rule?")) return;
    try { await api.delete(`/proxy-rules/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startEdit = (r) => {
    const { rule_id, created_at, updated_at, ...rest } = r;
    setForm(rest); setEditing(rule_id); setOpen(true);
  };

  return (
    <Layout title="Reverse Proxy Rules" subtitle="PROXY">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="proxy-count">
          {items.length} ROUTES · {items.filter((i) => i.tls).length} TLS-TERMINATED
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-proxy-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2">
              <Plus className="w-4 h-4" /> NEW ROUTE
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><Network className="w-4 h-4 text-cyan-400" /> {editing ? "Edit Route" : "Create Route"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2"><L>Name</L><Input data-testid="proxy-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
              <div><L>Protocol</L>
                <Select value={form.protocol} onValueChange={(v) => setForm({ ...form, protocol: v })}>
                  <SelectTrigger data-testid="proxy-protocol-select" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {["http", "https", "tcp", "ws"].map((o) => <SelectItem key={o} value={o} className="font-mono uppercase text-xs">{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><L>Listen Host</L><Input data-testid="proxy-host-input" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" value={form.listen_host} onChange={(e) => setForm({ ...form, listen_host: e.target.value })} /></div>
              <div><L>Listen Port</L><Input data-testid="proxy-port-input" type="number" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" value={form.listen_port} onChange={(e) => setForm({ ...form, listen_port: parseInt(e.target.value || "0", 10) })} /></div>
              <div><L>Upstream</L><Input data-testid="proxy-upstream-input" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" value={form.upstream} onChange={(e) => setForm({ ...form, upstream: e.target.value })} /></div>
              <div><L>Rate limit (rps)</L><Input data-testid="proxy-rate-input" type="number" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" value={form.rate_limit_rps} onChange={(e) => setForm({ ...form, rate_limit_rps: parseInt(e.target.value || "0", 10) })} /></div>
              <div className="col-span-2 grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-900">
                  <span className="text-sm">TLS</span>
                  <Switch data-testid="proxy-tls-switch" checked={form.tls} onCheckedChange={(v) => setForm({ ...form, tls: v })} />
                </div>
                <div className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-900">
                  <span className="text-sm">Enabled</span>
                  <Switch data-testid="proxy-enabled-switch" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="submit-proxy-btn" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">{editing ? "SAVE" : "CREATE"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="proxy-list">
        {items.map((r) => (
          <div key={r.rule_id} data-testid={`proxy-card-${r.rule_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-display text-lg font-bold tracking-tight">{r.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{r.rule_id}</div>
              </div>
              <span className={"text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm " + (r.enabled ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-zinc-500 border-zinc-700")}>
                {r.enabled ? "ACTIVE" : "OFFLINE"}
              </span>
            </div>
            <div className="font-mono text-xs space-y-1.5 text-zinc-300">
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">PROTO</span><span className="text-cyan-400 uppercase">{r.protocol}</span> {r.tls && <span className="ml-2 text-amber-400">TLS</span>}</div>
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">LISTEN</span>{r.listen_host}:<span className="text-cyan-400">{r.listen_port}</span></div>
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">UPSTREAM</span>{r.upstream}</div>
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">RATE</span><span className="text-amber-400">{r.rate_limit_rps}</span> rps</div>
            </div>
            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-zinc-800">
              <Button size="sm" variant="ghost" data-testid={`edit-proxy-${r.rule_id}`} onClick={() => startEdit(r)} className="gap-1.5"><Pencil className="w-3.5 h-3.5" />Edit</Button>
              {user?.role === "admin" && (
                <Button size="sm" variant="ghost" data-testid={`delete-proxy-${r.rule_id}`} onClick={() => del(r.rule_id)} className="gap-1.5 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no proxy routes yet.</div>}
      </div>
    </Layout>
  );
}

function L({ children }) {
  return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>;
}
